// @ts-check
// Modelo "famílias e alunos" (docs/arquitetura-appcrm/08-contrato-importacao.md, seção 5): uma linha por aluno,
// com os responsáveis repetidos nas linhas da mesma família. Cada coluna aponta para um campo real do pacote
// educacional, então a conversão e a validação são as mesmas da importação de um objeto.
import { buildImportTargets } from './import-targets.js';
import { normalizeForMatching } from './text-matching.js';

/** @typedef {import('./import-targets.js').ImportTarget} TemplateTarget */
/** @typedef {import('./import-targets.js').ImportObjectMetadata} TemplateObjectMetadata */

export const FAMILY_TEMPLATE_ID = 'familyEnrollment';
export const FAMILY_GROUPS = Object.freeze(['household', 'guardian1', 'guardian1Member', 'guardian2', 'guardian2Member', 'student', 'opportunity', 'opportunityStudent']);

/** @typedef {{ header: string, group: string, objectNameSingular: string, fieldName: string, subFieldName?: string, aliases?: string[], example: [string, string, string] }} FamilyTemplateColumn */

/** @param {1 | 2} index @returns {FamilyTemplateColumn[]} */
const guardianColumns = (index) => {
  const group = `guardian${index}`;
  const prefix = `Responsável ${index}`;
  const first = index === 1;
  return [
    { header: prefix, group, objectNameSingular: 'person', fieldName: 'name', subFieldName: 'fullName',
      aliases: [`responsavel ${index} nome`, `nome do responsavel ${index}`, ...(first ? ['responsavel', 'nome do responsavel', 'responsavel principal'] : ['segundo responsavel'])],
      example: first ? ['Marta Prado', 'Marta Prado', 'Rafael Nunes'] : ['Paulo Prado', 'Paulo Prado', ''] },
    { header: `${prefix} parentesco`, group: `${group}Member`, objectNameSingular: 'householdMember', fieldName: 'kinship',
      aliases: first ? ['parentesco', 'grau de parentesco'] : [], example: first ? ['Mãe', 'Mãe', 'Pai'] : ['Pai', 'Pai', ''] },
    { header: `${prefix} e-mail`, group, objectNameSingular: 'person', fieldName: 'emails', subFieldName: 'primaryEmail',
      aliases: first ? ['email do responsavel', 'email responsavel', 'email'] : [],
      example: first ? ['marta.prado@email.example', 'marta.prado@email.example', 'rafael.nunes@email.example'] : ['paulo.prado@email.example', 'paulo.prado@email.example', ''] },
    { header: `${prefix} telefone`, group, objectNameSingular: 'person', fieldName: 'phones', subFieldName: 'primaryPhoneNumber',
      aliases: first ? ['telefone do responsavel', 'celular do responsavel', 'telefone', 'celular', 'whatsapp'] : [],
      example: first ? ['(19) 99876-5432', '(19) 99876-5432', '(11) 98888-7777'] : ['(19) 99123-4567', '(19) 99123-4567', ''] },
    { header: `${prefix} CPF`, group, objectNameSingular: 'person', fieldName: 'cpf', aliases: first ? ['cpf do responsavel', 'cpf responsavel'] : [],
      example: ['', '', ''] },
    { header: `${prefix} financeiro`, group: `${group}Member`, objectNameSingular: 'householdMember', fieldName: 'isFinancialGuardian',
      aliases: first ? ['responsavel financeiro'] : [], example: first ? ['Sim', 'Sim', 'Sim'] : ['Não', 'Não', ''] },
  ];
};

/** @type {ReadonlyArray<FamilyTemplateColumn>} */
export const FAMILY_TEMPLATE_COLUMNS = Object.freeze([
  { header: 'Família', group: 'household', objectNameSingular: 'household', fieldName: 'name', aliases: ['familia', 'nome da familia'],
    example: ['Família Prado', 'Família Prado', 'Família Nunes'] },
  // endereço da família (CEP na família, pacote 1.12.0): é o CEP que o mapa de calor lê; o bairro vai no complemento
  { header: 'CEP', group: 'household', objectNameSingular: 'household', fieldName: 'householdAddress', subFieldName: 'addressPostcode',
    aliases: ['cep', 'cep da familia', 'cep residencial', 'codigo postal'], example: ['13025-320', '13025-320', '13092-150'] },
  { header: 'Endereço', group: 'household', objectNameSingular: 'household', fieldName: 'householdAddress', subFieldName: 'addressStreet1',
    aliases: ['endereco', 'logradouro', 'rua', 'endereco da familia'],
    example: ['Av. José de Sousa Campos, 900', 'Av. José de Sousa Campos, 900', 'Rua Ângelo José Vicente, 45'] },
  { header: 'Bairro', group: 'household', objectNameSingular: 'household', fieldName: 'householdAddress', subFieldName: 'addressStreet2',
    aliases: ['bairro', 'complemento'], example: ['Cambuí', 'Cambuí', 'Nova Campinas'] },
  { header: 'Cidade', group: 'household', objectNameSingular: 'household', fieldName: 'householdAddress', subFieldName: 'addressCity',
    aliases: ['cidade', 'municipio'], example: ['Campinas', 'Campinas', 'Campinas'] },
  { header: 'UF', group: 'household', objectNameSingular: 'household', fieldName: 'householdAddress', subFieldName: 'addressState',
    aliases: ['uf', 'estado'], example: ['SP', 'SP', 'SP'] },
  ...guardianColumns(1),
  ...guardianColumns(2),
  { header: 'Aluno', group: 'student', objectNameSingular: 'person', fieldName: 'name', subFieldName: 'fullName',
    aliases: ['aluno nome', 'nome do aluno', 'aluna', 'aluno a', 'estudante', 'candidato', 'nome do estudante'], example: ['Ana Prado', 'Bruno Prado', 'Lucas Nunes'] },
  { header: 'Aluno data de nascimento', group: 'student', objectNameSingular: 'person', fieldName: 'birthDate',
    aliases: ['data de nascimento', 'nascimento', 'data de nascimento do aluno'], example: ['12/05/2016', '03/09/2019', '21/01/2012'] },
  { header: 'Aluno CPF', group: 'student', objectNameSingular: 'person', fieldName: 'cpf', aliases: ['cpf do aluno'], example: ['', '', ''] },
  { header: 'Aluno série atual', group: 'student', objectNameSingular: 'person', fieldName: 'currentGradeLevel',
    aliases: ['serie atual', 'ano atual', 'serie atual do aluno'], example: ['', '', ''] },
  { header: 'Escola de origem', group: 'student', objectNameSingular: 'person', fieldName: 'originSchool',
    aliases: ['escola atual', 'escola anterior'], example: ['Escola Municipal Centro', '', 'Colégio Exemplo'] },
  { header: 'Curso pretendido', group: 'opportunityStudent', objectNameSingular: 'opportunityStudent', fieldName: 'course',
    aliases: ['curso', 'serie pretendida', 'curso de interesse', 'ano pretendido'], example: ['', '', ''] },
  { header: 'Turma', group: 'opportunityStudent', objectNameSingular: 'opportunityStudent', fieldName: 'classOffering',
    aliases: ['turma pretendida'], example: ['', '', ''] },
  { header: 'Valor previsto', group: 'opportunityStudent', objectNameSingular: 'opportunityStudent', fieldName: 'amount', subFieldName: 'amountMicros',
    aliases: ['valor', 'mensalidade', 'valor da mensalidade'], example: ['2.500,00', '2.300,00', '2.900,00'] },
  { header: 'Etapa', group: 'opportunity', objectNameSingular: 'enrollmentOpportunity', fieldName: 'stage',
    aliases: ['etapa do funil', 'status', 'situacao'], example: ['Lead', 'Lead', 'Visita agendada'] },
  { header: 'Unidade', group: 'opportunity', objectNameSingular: 'enrollmentOpportunity', fieldName: 'campus',
    aliases: ['unidade de interesse', 'campus'], example: ['', '', ''] },
  { header: 'Período letivo', group: 'opportunity', objectNameSingular: 'enrollmentOpportunity', fieldName: 'academicPeriod',
    aliases: ['ano letivo', 'periodo', 'periodo pretendido'], example: ['', '', ''] },
  { header: 'Origem', group: 'opportunity', objectNameSingular: 'enrollmentOpportunity', fieldName: 'leadSource',
    aliases: ['origem do lead', 'canal', 'como conheceu'], example: ['Indicação', 'Indicação', 'Instagram'] },
  { header: 'Consultor(a)', group: 'opportunity', objectNameSingular: 'enrollmentOpportunity', fieldName: 'owner',
    aliases: ['consultor', 'consultora', 'responsavel comercial', 'vendedor'], example: ['', '', ''] },
  { header: 'Motivo da perda', group: 'opportunity', objectNameSingular: 'enrollmentOpportunity', fieldName: 'lostReason',
    aliases: ['motivo de perda'], example: ['', '', ''] },
  { header: 'Observações', group: 'household', objectNameSingular: 'household', fieldName: 'notes',
    aliases: ['observacao', 'obs', 'anotacoes'], example: ['Dois filhos, querem o período integral', '', ''] },
]);

/**
 * Destinos do modelo de famílias a partir dos metadados do tenant; colunas cujo campo não existe são omitidas.
 * @param {(nameSingular: string) => TemplateObjectMetadata | undefined} findObject
 * @returns {TemplateTarget[]}
 */
export function buildFamilyTemplateTargets(findObject) {
  /** @type {Map<string, Map<string, TemplateTarget>>} */
  const targetsByGroup = new Map();
  /** @type {TemplateTarget[]} */
  const targets = [];
  for (const column of FAMILY_TEMPLATE_COLUMNS) {
    let byKey = targetsByGroup.get(column.group);
    if (!byKey) {
      const objectMetadata = findObject(column.objectNameSingular);
      byKey = new Map((objectMetadata ? buildImportTargets(objectMetadata, { group: column.group }) : []).map((target) => [target.key, target]));
      targetsByGroup.set(column.group, byKey);
    }
    const target = byKey.get(column.subFieldName ? `${column.fieldName}.${column.subFieldName}` : column.fieldName);
    if (!target) continue;
    targets.push({
      ...target,
      key: `${column.group}.${target.key}`,
      aliases: [...new Set([column.header, ...(column.aliases ?? [])].map(normalizeForMatching))],
      isRequired: false,
      header: column.header,
    });
  }
  return targets;
}

/** Linhas do arquivo de exemplo (cabeçalho e três alunos de duas famílias). */
export function buildFamilyTemplateSampleRows() {
  return [
    FAMILY_TEMPLATE_COLUMNS.map((column) => column.header),
    ...[0, 1, 2].map((index) => FAMILY_TEMPLATE_COLUMNS.map((column) => column.example[index])),
  ];
}

/**
 * Chave que junta as linhas de uma família: nome da família; senão e-mail, telefone ou nome do responsável 1.
 * @param {Record<string, { values: Record<string, any>, relations: Record<string, string> }>} groups
 * @param {number} rowNumber
 */
export function familyKeyOf(groups, rowNumber) {
  const householdName = normalizeForMatching(groups.household?.values?.name);
  if (householdName) return `household:${householdName}`;
  const guardian = groups.guardian1?.values ?? {};
  const email = String(guardian.emails?.primaryEmail ?? '').toLowerCase();
  if (email) return `email:${email}`;
  const phone = String(guardian.phones?.primaryPhoneNumber ?? '');
  if (phone) return `phone:${phone}`;
  const name = normalizeForMatching([guardian.name?.firstName, guardian.name?.lastName].filter(Boolean).join(' '));
  if (name) return `guardian:${name}`;
  return `row:${rowNumber}`;
}
